// App records the page has seen, kept live by "appChange" events. Cards and full views read from here; each
// subscribes to just the collection it shows, so a vote re-renders that card, not the message list.
import { useEffect, useSyncExternalStore } from "react";
import type { AppRecord } from "@bloom/shared";

const key = (app: string, collection: string) => `${app}/${collection}`;

export class AppData {
  /** app/collection → records by id (absent: not loaded yet). */
  private colls = new Map<string, Map<string, AppRecord>>();
  /** Stable arrays for useSyncExternalStore, rebuilt when a collection changes. */
  private arrays = new Map<string, AppRecord[]>();
  private listeners = new Map<string, Set<() => void>>();
  /** Per app: who wants each change itself (full-view frames get them record by record). */
  private changeListeners = new Map<string, Set<(collection: string, id: string | null, record?: AppRecord) => void>>();

  /** Hears each change to an app's loaded records; `id` null: the whole collection was (re)loaded. */
  onChange(app: string, fn: (collection: string, id: string | null, record?: AppRecord) => void) {
    let set = this.changeListeners.get(app);
    if (!set) this.changeListeners.set(app, (set = new Set()));
    set.add(fn);
    return () => void set!.delete(fn);
  }

  list(app: string, collection: string): AppRecord[] | undefined {
    const k = key(app, collection);
    if (!this.colls.has(k)) return undefined;
    let arr = this.arrays.get(k);
    if (!arr) this.arrays.set(k, (arr = [...this.colls.get(k)!.values()]));
    return arr;
  }

  get(app: string, collection: string, id: string): AppRecord | undefined {
    return this.colls.get(key(app, collection))?.get(id);
  }

  /** A whole collection arrived (appRecords). */
  setAll(app: string, collection: string, records: AppRecord[]) {
    this.colls.set(key(app, collection), new Map(records.map((r) => [r.id, r])));
    this.changed(app, collection);
    for (const fn of this.changeListeners.get(app) ?? []) fn(collection, null);
  }

  /** One record changed or was deleted (appChange). Ignored for collections never loaded here. */
  apply(app: string, collection: string, id: string, record?: AppRecord) {
    const m = this.colls.get(key(app, collection));
    if (!m) return;
    if (record) m.set(id, record);
    else m.delete(id);
    this.changed(app, collection);
    for (const fn of this.changeListeners.get(app) ?? []) fn(collection, id, record);
  }

  subscribe(app: string, collection: string, fn: () => void) {
    const k = key(app, collection);
    let set = this.listeners.get(k);
    if (!set) this.listeners.set(k, (set = new Set()));
    set.add(fn);
    return () => void set!.delete(fn);
  }

  private changed(app: string, collection: string) {
    const k = key(app, collection);
    this.arrays.delete(k);
    for (const fn of this.listeners.get(k) ?? []) fn();
  }
}

/** What a component needs to read live app data: the store, and a way to start watching a collection. */
export interface AppDataSource {
  data: AppData;
  /** Loads the collection and keeps it live while watched. Returns the unwatch. */
  watchCollection: (app: string, collection: string) => () => void;
}

/** A collection's records, live (undefined while loading). */
export function useCollection(src: AppDataSource | null | undefined, app: string, collection: string): AppRecord[] | undefined {
  useEffect(() => (src ? src.watchCollection(app, collection) : undefined), [src, app, collection]);
  return useSyncExternalStore(
    (fn) => (src ? src.data.subscribe(app, collection, fn) : () => {}),
    () => src?.data.list(app, collection),
  );
}

/** One record, live. */
export function useRecord(src: AppDataSource | null | undefined, app: string, collection: string, id: string): AppRecord | undefined {
  const list = useCollection(src, app, collection);
  return list ? src!.data.get(app, collection, id) : undefined;
}
